/**
 * useAlerts — polls the production alerts feed every 60s and exposes
 * a small imperative API for the AlertsCenter.
 *
 * GP 1.0 · Sprint E · Feb 2026
 *
 * Contract:
 *   const { alerts, count, loading, refetch, removeLocal } = useAlerts();
 *
 *   • alerts       list of AlertOut from the backend
 *   • count        alerts.length (for the bell badge)
 *   • loading      true while the first fetch is in-flight
 *   • refetch()    manual reconciliation; awaits the network
 *   • removeLocal(entity_id) optimistic removal (used after successful
 *                            "Create Work Order" in a row component)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";

const POLL_MS = 60_000;

// Feb 2026 · bugfix aditivo — Cada tipo de alerta declara los permisos
// oficiales necesarios para verla. Si el usuario no cumple, la alerta
// no aparece en el panel ni cuenta en el badge de la campana.
// `invoice_missing_wo` sólo es útil para roles que pueden crear una WO
// (endpoint `POST /commercial/invoices/{id}/create-work-order` está
// gateado por `_dep_admin_only` en el backend).
const ALERT_TYPE_ADMIN_ONLY = new Set(["invoice_missing_wo"]);

export default function useAlerts(enabled = true) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const intervalRef = useRef(null);
  const { isAdmin } = useAuth();

  const refetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/alerts/production`, {
        withCredentials: true,
      });
      if (!mountedRef.current) return;
      setAlerts(Array.isArray(data) ? data : []);
    } catch (_e) {
      // Silent failure — keep last known list to avoid flicker.
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const removeLocal = useCallback((entityId) => {
    setAlerts((prev) => prev.filter((a) => a.entity_id !== entityId));
  }, []);

  // Filtro por permisos oficiales: si el usuario no es admin, ocultamos
  // los tipos admin-only. El backend seguirá devolviendo esos ítems (no
  // se toca), pero el hook los descarta en frontend antes de exponerlos.
  const visibleAlerts = useMemo(
    () =>
      isAdmin
        ? alerts
        : alerts.filter((a) => !ALERT_TYPE_ADMIN_ONLY.has(a.alert_type)),
    [alerts, isAdmin],
  );

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }
    refetch();
    intervalRef.current = setInterval(refetch, POLL_MS);
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, refetch]);

  return {
    alerts: visibleAlerts,
    count: visibleAlerts.length,
    loading,
    refetch,
    removeLocal,
  };
}
